import React, { forwardRef, useMemo } from 'react';
import { Input } from 'antd';
import { useValueRef } from '../../../../../../hooks/useValueRef';
import SelectStreets from './SelectStreets';
import SelectHouses from './SelectHouses';
import SelectRooms from './SelectRooms';
import { indexes } from './config';
import { useStreetsQuery, useHousesQuery, useRoomsQuery } from './api';
import { useDebouncedIds } from './hooks/useDebouncedIds';
import {
  getHousesQuerySelect,
  roomsQuerySelect,
  streetsQuerySelect,
} from './utils';
import styles from './index.module.scss';

const AddressFieldContainer = ({ value, onChange, loading }, ref) => {
  const {
    [indexes.street]: streetIds,
    [indexes.house]: houseIds,
    [indexes.room]: roomIds,
  } = value || [];

  const propsRef = useValueRef({ value: value || [], onChange });

  // streets
  const { data: streetsData, isFetching: isStreetsFetching } = useStreetsQuery({
    options: { staleTime: Infinity, select: streetsQuerySelect },
  });
  const streetDebouncedIds = useDebouncedIds(streetIds);

  // houses
  const housesQuerySelect = useMemo(
    () => getHousesQuerySelect(streetsData?.map),
    [streetsData]
  );

  const { data: housesData, isFetching: isHousesFetching } = useHousesQuery({
    params: { streetIds: streetDebouncedIds },
    options: {
      staleTime: Infinity,
      enabled: Boolean(streetDebouncedIds?.length),
      select: housesQuerySelect,
    },
  });
  const housesDebouncedIds = useDebouncedIds(houseIds);

  // rooms
  const { data: roomsData, isFetching: isRoomsFetching } = useRoomsQuery({
    params: { houseIds: housesDebouncedIds },
    options: {
      staleTime: Infinity,
      enabled: Boolean(housesDebouncedIds?.length),
      select: roomsQuerySelect,
    },
  });

  return (
    <Input.Group
      compact
      className={styles.root}
    >
      <SelectStreets
        options={streetsData?.options}
        value={streetIds}
        propsRef={propsRef}
        loading={loading || isStreetsFetching}
      />
      <SelectHouses
        options={housesData?.options}
        ids={housesData?.ids}
        value={houseIds}
        propsRef={propsRef}
        loading={loading || isHousesFetching}
        map={streetsData?.map}
        notFoundContent={streetIds?.length ? 'Нет данных' : 'Выберите улицы'}
      />
      <SelectRooms
        options={roomsData?.options}
        ids={roomsData?.ids}
        value={roomIds}
        propsRef={propsRef}
        loading={loading || isRoomsFetching}
        map={housesData?.map}
        notFoundContent={houseIds?.length ? 'Нет данных' : 'Выберите дома'}
      />
    </Input.Group>
  );
};

export const AddressField = forwardRef(AddressFieldContainer);
